import React from "react";
import './IntegrationsSection.css'
import { FaWhatsapp, FaTelegramPlane, FaExternalLinkAlt, FaSlack } from "react-icons/fa";
import { SiZoho } from "react-icons/si";

const IntegrationsSection = ({ bot }) => {
  const email = bot?.email || "";
  const botName = bot?.botName || "";

  const handleRequest = (channel) => {
    const subject = `${channel} integration request for "${botName || "my bot"}"`;
    const bodyLines = [
      `Hi XenaChat team,`,
      "",
      `I would like to enable ${channel} integration for the following bot:`,
      `- Bot name: ${botName || "(not set)"}`,
      `- Owner email: ${email || "(not set)"}`,
      "",
      "Please help me with the steps and required details.",
      "",
      "Thanks,"
    ];
    const mailto = `mailto:support@xenachat.ai?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
    window.location.href = mailto;
  };

  const openDocs = () => {
    window.open("/docs#telegram-whatsApp-integration", "_blank");
  };

  return (
    <div className="xc-int-wrapper">
      <h2 className="xc-int-heading">Integrations</h2>
      <p className="xc-int-subtitle">
        Let this bot answer users directly on WhatsApp and Telegram.  
        For now, our team sets up the connection manually based on your request.
      </p>

      <div className="xc-int-grid">
        {/* WhatsApp card */}
        <div className="xc-int-card">
          <div className="xc-int-card-header">
            <div className="xc-int-card-title-row">
              <div className="xc-int-card-channel">
                <span className="xc-int-status-icon xc-int-status-wa">
                  <FaWhatsapp />
                </span>
                <span className="xc-int-card-title">WhatsApp</span>
              </div>
              <span className="xc-int-chip">Manual setup</span>
            </div>

            <p className="xc-int-card-desc">
              Connect this bot to your WhatsApp Business number using the
              WhatsApp Cloud API. Our team wires the webhook to your bot and
              handles all the technical configuration.
            </p>
          </div>

          <ul className="xc-int-bullets">
            <li>Uses the same knowledge base & settings as this bot</li>
            <li>Messages are logged in your XenaChat Inbox & Analytics</li>
            <li>Best for support, FAQs, and lead qualification on WhatsApp</li>
          </ul>

          <div className="xc-int-card-footer">
            <button
              type="button"
              className="xc-int-primary-btn"
              onClick={() => handleRequest("WhatsApp")}
            >
              Request WhatsApp integration
            </button>
            <button
              type="button"
              className="xc-int-link-btn"
              onClick={openDocs}
            >
              View tech details <FaExternalLinkAlt className="xc-int-link-icon" />
            </button>
          </div>
        </div>

        {/* Telegram card */}
        <div className="xc-int-card">
          <div className="xc-int-card-header">
            <div className="xc-int-card-title-row">
              <div className="xc-int-card-channel">
                <span className="xc-int-status-icon xc-int-status-tg">
                  <FaTelegramPlane />
                </span>
                <span className="xc-int-card-title">Telegram</span>
              </div>
              <span className="xc-int-chip xc-int-chip-soon">Manual setup</span>
            </div>

            <p className="xc-int-card-desc">
              Let users chat with this bot from a Telegram bot. We connect your
              Telegram bot token to XenaChat and route all messages through the
              same FastAPI engine.
            </p>
          </div>

          <ul className="xc-int-bullets">
            <li>Reusable across multiple groups or channels</li>
            <li>Same rate limits & message limits as your web widget</li>
            <li>Ideal for communities and product support channels</li>
          </ul>

          <div className="xc-int-card-footer">
            <button
              type="button"
              className="xc-int-primary-btn"
              onClick={() => handleRequest("Telegram")}
            >
              Request Telegram integration
            </button>
            <button
              type="button"
              className="xc-int-link-btn"
              onClick={openDocs}
            >
              View tech details <FaExternalLinkAlt className="xc-int-link-icon" />
            </button>
          </div>
        </div>

        {/* Slack card */}
        <div className="xc-int-card">
          <div className="xc-int-card-header">
            <div className="xc-int-card-title-row">
              <div className="xc-int-card-channel">
                <span className="xc-int-status-icon xc-int-status-sl">
                  <FaSlack />
                </span>
                <span className="xc-int-card-title">Slack</span>
              </div>
              <span className="xc-int-chip">Manual setup</span>
            </div>

            <p className="xc-int-card-desc">
              Connect this bot to your Slack workspace using the Slack API. Our team wires the webhook to your bot and
              handles all the technical configuration.
            </p>
          </div>

          <ul className="xc-int-bullets">
            <li>Chat with your AI bot directly inside Slack channels or DMs</li>
            <li>Useful for internal support, team knowledge, and quick answers</li>
            <li>Messages and usage are logged in your XenaChat analytics</li>
          </ul>

          <div className="xc-int-card-footer">
            <button
              type="button"
              className="xc-int-primary-btn"
              onClick={() => handleRequest("Slack")}
            >
              Request Slack integration
            </button>
            <button
              type="button"
              className="xc-int-link-btn"
              onClick={openDocs}
            >
              View tech details <FaExternalLinkAlt className="xc-int-link-icon" />
            </button>
          </div>
        </div>

        {/* Zoho card */}
        <div className="xc-int-card">
          <div className="xc-int-card-header">
            <div className="xc-int-card-title-row">
              <div className="xc-int-card-channel">
                <span className="xc-int-status-icon xc-int-status-wa">
                  <SiZoho />
                </span>
                <span className="xc-int-card-title">Zoho</span>
              </div>
              <span className="xc-int-chip">Manual setup</span>
            </div>

            <p className="xc-int-card-desc">
              Connect this bot to your Zoho CRM using the Zoho API. Our team wires the webhook to your bot and
              handles all the technical configuration.
            </p>
          </div>

          <ul className="xc-int-bullets">
            <li>Automatically create leads or contacts from chatbot conversations</li>
            <li>Sync customer information with Zoho CRM records</li>
            <li>Useful for sales qualification and CRM automation</li>
          </ul>

          <div className="xc-int-card-footer">
            <button
              type="button"
              className="xc-int-primary-btn"
              onClick={() => handleRequest("Zoho")}
            >
              Request Zoho integration
            </button>
            <button
              type="button"
              className="xc-int-link-btn"
              onClick={openDocs}
            >
              View tech details <FaExternalLinkAlt className="xc-int-link-icon" />
            </button>
          </div>
        </div>
      </div>

      <div className="xc-int-note">
        Once we enable an integration for your account, your bot will start
        responding on that channel using the same retrieval, limits and
        analytics as the web widget.
      </div>
    </div>
  );
};

export default IntegrationsSection;
